import { useMemo } from "react";
import { useParams } from "react-router";

import { useSearchArrayParam } from "@/common/hooks/useSearchParam";
import { useSegments } from "@/company/data/useSegments";

export const useSubsegmentParam = (fallback?: string[] | null) => {
  const { data: segments, isLoading } = useSegments();

  const allowlist = useMemo(
    () => (segments ? segments.map((segment) => segment.id) : []),
    [segments],
  );

  const fallbackSegments = useMemo(() => {
    if (!fallback?.length) return undefined;
    return fallback.flatMap((idOrName) => {
      const segmentId = segments?.find(
        (segment) => segment.id === idOrName || segment.name === idOrName,
      )?.id;
      return segmentId ? [segmentId] : [];
    });
  }, [fallback, segments]);

  return [
    ...useSearchArrayParam("subsegment", {
      fallback: fallbackSegments,
      allowlist,
    }),
    isLoading,
  ] as const;
};

const validSlugs = {
  env: "en",
  org: "or",
  feature: "fe",
  segment: "se",
};
type ValidSlug = keyof typeof validSlugs;

type SlugParam<T extends ValidSlug> = { [key in `${T}Id`]?: string } & {
  [key in `${T}Name`]?: string;
};

/**
 * React hook to extract a slug parameter from the URL and split it into a name and ID.
 *
 * This hook expects a route parameter in the format `${param}Slug` (e.g., "featureSlug" for param="feature"),
 * where the value is a string like "huddles-fe324sf13f234x" or "fe324sf13f234x". It splits based on the last 14 characters,
 * returning an object with `${param}Name` and `${param}Id` properties.
 *
 * @template T - The base name of the parameter (e.g., "feature" for "featureSlug").
 * @param {T} param - The base name of the slug parameter to extract from the URL.
 * @returns {SlugParam<T>} An object containing `${param}Name` and `${param}Id` if the slug exists, otherwise an empty object.
 *
 * @example
 * // Given a route like "/features/:featureSlug" and a URL "/features/huddles-fe324sf13f234x"
 * const { featureName, featureId } = useSlugParam("feature");
 * // featureName === "huddles"
 * // featureId === "fe324sf13f234x"
 */
export const useSlugParam = <T extends ValidSlug>(param: T): SlugParam<T> => {
  const { [`${param}Slug`]: slug } = useParams();
  if (slug) {
    // Check if the slug has the correct format for this specific param type
    const prefix = validSlugs[param];
    const regex = new RegExp(`^(.+-)?${prefix}[A-Za-z0-9]{12}$`);

    if (!regex.test(slug)) {
      console.warn(`Invalid ${param} id: ${slug}`);
      return {
        [`${param}Id`]: slug,
      } as SlugParam<T>;
    }
    const id = slug.substring(slug.length - 14);
    const name = slug.substring(0, slug.length - 15);
    return {
      [`${param}Id`]: id,
      [`${param}Name`]: name || undefined,
    } as SlugParam<T>;
  }
  return {} as SlugParam<T>;
};
